import { useMemo, useState, useRef, useEffect } from 'react'
import {
  Box,
  Button,
  Divider,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createPortal } from 'react-dom'
import { Calendar, Clock } from 'lucide-react'
import { DayPicker } from 'react-day-picker'
import { format } from 'date-fns'
import 'react-day-picker/dist/style.css'

import { useThemeColors } from '@/hooks/useThemeColors'

type Props = {
  value?: { date?: Date; time?: string } // time = "HH:mm"
  onChange?: (next: { date?: Date; time?: string }) => void
  onApply?: (next: { date?: Date; time?: string }) => void
  placeholderDate?: string
  placeholderTime?: string
  suggestedTimes?: string[]
}

const DEFAULT_SUGGESTED = ['09:00', '11:00', '13:00', '15:00', '16:00']
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

function toDisplayTime(hhmm?: string) {
  if (!hhmm) return ''
  const [hStr, m] = hhmm.split(':')
  const h = Number(hStr)
  const isPM = h >= 12
  const h12 = ((h + 11) % 12) + 1
  return `${h12}:${m} ${isPM ? 'PM' : 'AM'}`
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}
function isValidDate(d: Date) {
  return !Number.isNaN(d.getTime())
}

/**
 * Accepts:
 * - "31 / Jan / 2026"
 * - "31/Jan/2026"
 * - "31/01/2026"
 * - "31 / January / 2026"
 */
function parseTypedDate(input: string): Date | null {
  const raw = input.trim()
  if (!raw) return null

  const parts = raw.replace(/\s+/g, '').split('/').filter(Boolean)
  if (parts.length !== 3) return null

  const [ddStr, mmStr, yyyyStr] = parts
  const dd = Number(ddStr)
  const yyyy = Number(yyyyStr)
  if (!Number.isFinite(dd) || !Number.isFinite(yyyy) || dd < 1 || dd > 31) return null

  let monthIndex = -1

  // numeric month "01".."12"
  if (/^\d{1,2}$/.test(mmStr)) {
    const mm = Number(mmStr)
    if (mm >= 1 && mm <= 12) monthIndex = mm - 1
  } else {
    // month name "Jan" / "January"
    const key = mmStr.toLowerCase()
    monthIndex = MONTHS.findIndex(
      (m) => m.toLowerCase() === key || m.toLowerCase().startsWith(key),
    )
  }

  if (monthIndex < 0) return null

  const d = new Date(yyyy, monthIndex, dd)
  if (d.getFullYear() !== yyyy || d.getMonth() !== monthIndex || d.getDate() !== dd) return null

  return d
}

export function DateTimePickerPopover({
  value,
  onChange,
  onApply,
  placeholderDate = 'Select date',
  placeholderTime = 'Select time',
  suggestedTimes = DEFAULT_SUGGESTED,
}: Props) {
  const colors = useThemeColors()

  // ✅ controlled popover so Cancel/Apply can close it
  const [isOpen, setIsOpen] = useState(false)

  const [draftDate, setDraftDate] = useState<Date | undefined>(value?.date)
  const [draftTime, setDraftTime] = useState<string | undefined>(value?.time)

  const initial = useMemo(
    () => ({ date: value?.date, time: value?.time }),
    [value?.date, value?.time],
  )

  // ✅ typeable date input
  const [dateText, setDateText] = useState<string>(
    value?.date ? format(value.date, 'dd / MMM / yyyy') : '',
  )
  const [dateError, setDateError] = useState<string>('')

  const displayDate = useMemo(() => {
    return value?.date ? format(value.date, 'dd / MMM / yyyy') : ''
  }, [value?.date])

  const displayTime = useMemo(() => {
    return value?.time ? toDisplayTime(value.time) : ''
  }, [value?.time])

  // Disable past dates (today allowed)
  const disabledBefore = useMemo(() => startOfDay(new Date()), [])

  function commitDateText(nextText?: string) {
    const text = (nextText ?? dateText).trim()

    if (!text) {
      setDateError('')
      setDraftDate(undefined)
      return
    }

    const parsed = parseTypedDate(text)
    if (!parsed || !isValidDate(parsed)) {
      setDateError('Invalid date format')
      return
    }

    if (parsed < disabledBefore) {
      setDateError('Date cannot be in the past')
      return
    }

    setDateError('')
    setDraftDate(parsed)
    setDateText(format(parsed, 'dd / MMM / yyyy'))
  }

  function openPopover() {
    setIsOpen(true)
    setDraftDate(initial.date)
    setDraftTime(initial.time)
    setDateText(initial.date ? format(initial.date, 'dd / MMM / yyyy') : '')
    setDateError('')
  }

  function closePopover() {
    setIsOpen(false)
  }

  function handleCancel() {
    setDraftDate(initial.date)
    setDraftTime(initial.time)
    setDateText(initial.date ? format(initial.date, 'dd / MMM / yyyy') : '')
    setDateError('')
    closePopover() // ✅ close on cancel
  }

  function handleApply() {
    // commit typed date first
    commitDateText()

    if (!draftDate || !draftTime || dateError) return

    const next = { date: draftDate, time: draftTime }
    onChange?.(next)
    onApply?.(next)
    closePopover() // ✅ close on apply
  }

  return (
    <Popover
      placement="bottom-start"
      closeOnBlur={false}
      isOpen={isOpen}
      onOpen={openPopover}
      onClose={closePopover}
    >
      <PopoverTrigger>
        <HStack
          w="100%"
          minW="200px"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          px={3}
          py={2}
          spacing={2}
          _hover={{ borderColor: 'gray.300' }}
          cursor="pointer"
          whiteSpace="nowrap"
          flexWrap="nowrap"
        >
          <HStack flex="1 1 auto" spacing={2} minW={0}>
            <Box flexShrink={0}><Calendar size={16} /></Box>
            <Text fontSize="sm" color={displayDate ? 'gray.700' : 'gray.400'} noOfLines={1} overflow="hidden" textOverflow="ellipsis">
              {displayDate || placeholderDate}
            </Text>
          </HStack>
          <Divider orientation="vertical" h="18px" flexShrink={0} />
          <HStack flex="1 1 auto" spacing={2} minW={0} justify="flex-start">
            <Box flexShrink={0}><Clock size={16} /></Box>
            <Text fontSize="sm" color={displayTime ? 'gray.700' : 'gray.400'} noOfLines={1} overflow="hidden" textOverflow="ellipsis">
              {displayTime || placeholderTime}
            </Text>
          </HStack>
        </HStack>
      </PopoverTrigger>

      <Portal appendToParentPortal={false}>
        <PopoverContent 
          w="520px" 
          p={0} 
          borderRadius="md" 
          overflow="hidden" 
          zIndex={9999}
          boxShadow="xl"
        >
        <PopoverBody p={0}>
          <HStack align="stretch" spacing={0}>
            {/* LEFT: Date */}
            <Box p={4} w="300px">
              <InputGroup mb={2}>
                <InputLeftElement pointerEvents="none">
                  <Calendar size={16} />
                </InputLeftElement>

                <Input
                  value={dateText}
                  onChange={(e) => {
                    setDateText(e.target.value)
                    if (dateError) setDateError('')
                  }}
                  onBlur={() => commitDateText()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      commitDateText()
                    }
                  }}
                  placeholder="dd / MMM / yyyy"
                  // ✅ remove blue focus, use black
                  focusBorderColor="gray.900"
                  borderColor={dateError ? colors.error : undefined}
                />
              </InputGroup>

              {dateError ? (
                <Text fontSize="xs" color={colors.error} mb={2}>
                  {dateError}
                </Text>
              ) : (
                <Text fontSize="xs" color="gray.400" mb={2}>
                  Format: dd / MMM / yyyy (e.g., 31 / Jan / 2026)
                </Text>
              )}

              <Box
                sx={{
                  // ✅ DayPicker root + vars
                  '.rdp-root': {
                    margin: 0,
                    '--rdp-accent-color': '#000000',
                    '--rdp-accent-background-color': 'transparent',
                  },

                  // header label weight
                  '.rdp-caption_label': { fontWeight: 600 },

                  // ✅ Navigation arrows BLACK
                  '.rdp-button_previous, .rdp-button_next': {
                    color: '#000000',
                  },
                  '.rdp-chevron': {
                    fill: 'currentColor',
                  },

                  // ✅ Selected day - BLACK circle/ring
                  '[aria-selected="true"] .rdp-day_button': {
                    backgroundColor: 'transparent',
                    color: '#000000',
                    border: '2px solid #000000',
                    borderRadius: '50%',
                    fontWeight: 'bold',
                  },

                  // ✅ Hover state for non-selected days
                  '.rdp-day_button:hover:not([disabled])': {
                    backgroundColor: 'rgba(0,0,0,0.06)',
                  },

                  // ✅ Today's date styling
                  '.rdp-today:not(.rdp-outside)': {
                    fontWeight: 'bold',
                    color: '#000000',
                  },

                  // ✅ Keyboard focus ring BLACK
                  '.rdp-day_button:focus-visible': {
                    outline: '2px solid #000000',
                    outlineOffset: '2px',
                    borderRadius: '50%',
                  },
                }}
              >
                <DayPicker
                  mode="single"
                  selected={draftDate}
                  onSelect={(d: Date | undefined) => {
                    const next = d ?? undefined
                    setDraftDate(next)
                    setDateError('')
                    setDateText(next ? format(next, 'dd / MMM / yyyy') : '')
                  }}
                  disabled={{ before: disabledBefore }}
                />
              </Box>
            </Box>

            <Divider orientation="vertical" />

            {/* RIGHT: Time */}
            <Box p={4} flex="1">
              <Text fontSize="sm" fontWeight="700" mb={3} color="gray.700">
                Suggested Times
              </Text>

              <VStack align="stretch" spacing={2}>
                {suggestedTimes.map((t) => {
                  const isActive = draftTime === t
                  return (
                    <Button
                      key={t}
                      variant={isActive ? 'solid' : 'outline'}
                      onClick={() => setDraftTime(t)}
                      justifyContent="space-between"
                      bg={isActive ? colors.primary : undefined}
                      _hover={isActive ? { bg: colors.primaryHover } : undefined}
                      color={isActive ? 'white' : undefined}
                    >
                      {toDisplayTime(t)}
                    </Button>
                  )
                })}

                <Divider my={2} />

                <Text fontSize="xs" color="gray.500">
                  Or pick a custom time
                </Text>

                <Input
                  type="time"
                  value={draftTime ?? ''}
                  onChange={(e) => setDraftTime(e.target.value)}
                  // ✅ remove blue focus, use black
                  focusBorderColor="gray.900"
                />
              </VStack>
            </Box>
          </HStack>

          <Divider />

          {/* Footer */}
          <HStack justify="flex-end" p={3} spacing={3}>
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>

            <Button
              bg={colors.primary}
              _hover={{ bg: colors.primaryHover }}
              color="white"
              onClick={handleApply}
              isDisabled={!draftDate || !draftTime || !!dateError}
            >
              Apply
            </Button>
          </HStack>
        </PopoverBody>
      </PopoverContent>
      </Portal>
    </Popover>
  )
}

export default DateTimePickerPopover
